// Admin → Team: who can sign in here and what each person may do. People: invite (an email with a
// 3-day setup link), change roles, resend an invite, disable or enable, reset password and 2FA,
// remove. Roles: the built-in ones (read-only, they follow the code) and custom ones built from the
// permission catalogue. The API (routes/admin/team.ts) enforces every rule; this page only offers
// what the person looking may do.
import { useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Ban,
  Check,
  ChevronRight,
  KeyRound,
  Mail,
  Plus,
  Power,
  ShieldCheck,
  UserMinus,
  UserPlus,
  Users as UsersIcon,
} from 'lucide-react';
import {
  ALL_PERMISSIONS,
  PERMISSIONS,
  POWERFUL_PERMISSIONS,
  type Permission,
} from '@gaintplay/core/permissions';
import { api } from '../lib/api';
import { staffQuery } from '../lib/staff';
import {
  confirmAction,
  Drawer,
  EmptyState,
  ErrorNote,
  Initial,
  KV,
  PageHeader,
  Pill,
  ScrollX,
  SkeletonRows,
  Tabs,
  TimeAgo,
  ViewOnly,
  run,
  toast,
} from '../components/ui';

type Status = 'invited' | 'active' | 'disabled' | 'removed';
interface Person {
  id: string;
  name: string;
  email: string;
  status: Status;
  service: boolean;
  roles: string[];
  /** Password and 2FA set up. */
  twoFactor: boolean;
  lastSignInAt: number | null;
  invitedAt: number;
  invitedBy: string | null;
  removedAt: number | null;
  you: boolean;
  /** Whether the person looking may change them at all. */
  manageable: boolean;
}
interface Role {
  id: string;
  name: string;
  description: string | null;
  builtIn: boolean;
  permissions: Permission[];
  members: number;
  grantable: boolean;
  editable: boolean;
}
interface TeamData {
  me: string;
  owner: boolean;
  removed: number;
  staff: Person[];
  roles: Role[];
}

const OWNER = 'owner';
const STATUS: Record<Status, { label: string; tone: 'mint' | 'sky' | 'berry' | 'neutral' }> = {
  active: { label: 'Active', tone: 'mint' },
  invited: { label: 'Invited', tone: 'sky' },
  disabled: { label: 'Disabled', tone: 'berry' },
  removed: { label: 'Removed', tone: 'neutral' },
};

/** The catalogue by area, in its own order (Overview, Users, Cash-outs…). */
const AREAS = ALL_PERMISSIONS.reduce<{ area: string; perms: Permission[] }[]>((out, p) => {
  const area = PERMISSIONS[p].area;
  const group = out.find((g) => g.area === area);
  if (group) group.perms.push(p);
  else out.push({ area, perms: [p] });
  return out;
}, []);

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const holders = (n: number) => (n ? plural(n, 'person', 'people') : 'Nobody');
const reach = (r: Role) =>
  r.id === OWNER ? 'Every permission' : plural(r.permissions.length, 'permission');

function useTeam(removed: boolean) {
  return useQuery({
    queryKey: ['team', removed],
    queryFn: () => api<TeamData>(`/team${removed ? '?removed=1' : ''}`),
  });
}

export function Team() {
  const [params, setParams] = useSearchParams();
  const view = params.get('view') === 'roles' ? 'roles' : 'people';
  const showRemoved = params.get('removed') === '1';
  const set = (k: string, v: string | null) =>
    setParams(
      (p) => {
        if (v) p.set(k, v);
        else p.delete(k);
        return p;
      },
      { replace: k === 'view' || k === 'removed' },
    );
  const [inviting, setInviting] = useState(false);
  const { data, error, isLoading, refetch } = useTeam(showRemoved);
  const person = data?.staff.find((s) => s.id === params.get('person'));
  const roleParam = params.get('role');
  const role = roleParam === 'new' ? null : data?.roles.find((r) => r.id === roleParam);
  const people = data?.staff.filter((s) => s.status !== 'removed').length ?? 0;

  return (
    <div className="space-y-5">
      <PageHeader
        title="Team"
        description="Who can sign in to the admin and what each person can do. Role changes apply on their next click; disabling or removing someone signs them out at once."
        actions={
          view === 'people' ? (
            <button className="btn-primary" onClick={() => setInviting(true)} disabled={!data}>
              <UserPlus size={16} aria-hidden="true" /> Invite someone
            </button>
          ) : (
            <button className="btn-primary" onClick={() => set('role', 'new')} disabled={!data}>
              <Plus size={16} aria-hidden="true" /> New role
            </button>
          )
        }
      />
      <Tabs
        label="Team sections"
        value={view}
        onChange={(k) => set('view', k === 'people' ? null : k)}
        items={[
          { key: 'people', label: 'People', count: data ? people : undefined },
          { key: 'roles', label: 'Roles', count: data?.roles.length },
        ]}
      />
      <ErrorNote error={error} onRetry={() => void refetch()} />
      {error && !data ? null : isLoading || !data ? (
        <div className="card overflow-hidden">
          <SkeletonRows rows={4} />
        </div>
      ) : view === 'people' ? (
        <PeopleList
          data={data}
          showRemoved={showRemoved}
          onToggleRemoved={() => set('removed', showRemoved ? null : '1')}
          onOpen={(id) => set('person', id)}
          onInvite={() => setInviting(true)}
        />
      ) : (
        <RolesList data={data} onOpen={(id) => set('role', id)} />
      )}
      {data && person && (
        <PersonDrawer key={person.id} p={person} data={data} onClose={() => set('person', null)} />
      )}
      {data && inviting && <InviteDrawer data={data} onClose={() => setInviting(false)} />}
      {data && roleParam && role !== undefined && (
        <RoleDrawer key={roleParam} role={role} data={data} onClose={() => set('role', null)} />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ People */

function RolePills({ ids, roles }: { ids: string[]; roles: Role[] }) {
  if (!ids.length) return <span className="text-[13px] text-stone">No roles</span>;
  return (
    <span className="flex flex-wrap gap-1.5">
      {ids.map((id) => (
        <Pill key={id} tone={id === OWNER ? 'ink' : 'neutral'}>
          {roles.find((r) => r.id === id)?.name ?? id}
        </Pill>
      ))}
    </span>
  );
}

function StatusLine({ p }: { p: Person }) {
  const s = STATUS[p.status];
  return (
    <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
      <Pill tone={s.tone}>{s.label}</Pill>
      {p.status === 'invited' ? (
        <span className="text-[12px] text-stone">
          Sent <TimeAgo t={p.invitedAt} />
        </span>
      ) : p.status === 'removed' ? (
        <span className="text-[12px] text-stone">{p.removedAt && <TimeAgo t={p.removedAt} />}</span>
      ) : (
        p.status === 'active' &&
        p.twoFactor && (
          <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-mint-deep">
            <ShieldCheck size={12} aria-hidden="true" /> 2FA
          </span>
        )
      )}
    </span>
  );
}

function Who({ p, onOpen }: { p: Person; onOpen: (id: string) => void }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onOpen(p.id);
      }}
      className="group flex min-w-0 items-center gap-2.5 rounded-[8px] text-left"
    >
      <Initial name={p.name} size={32} />
      <span className="min-w-0">
        <span className="flex items-center gap-1.5">
          <span className="truncate text-[14px] font-bold group-hover:underline group-hover:decoration-2 group-hover:underline-offset-2">
            {p.name}
          </span>
          {p.you && <Pill tone="sun">You</Pill>}
        </span>
        <span className="block truncate text-[12px] text-stone">{p.email}</span>
      </span>
    </button>
  );
}

function PeopleList({
  data,
  showRemoved,
  onToggleRemoved,
  onOpen,
  onInvite,
}: {
  data: TeamData;
  showRemoved: boolean;
  onToggleRemoved: () => void;
  onOpen: (id: string) => void;
  onInvite: () => void;
}) {
  // Active people first, then invites, then disabled; removed ones last when shown.
  const order: Record<Status, number> = { active: 0, invited: 1, disabled: 2, removed: 3 };
  const list = [...data.staff].sort(
    (a, b) => order[a.status] - order[b.status] || a.name.localeCompare(b.name),
  );
  const alone = list.length === 1 && list[0]!.you;
  return (
    <div className="card overflow-hidden">
      {alone ? (
        <EmptyState
          icon={UsersIcon}
          title="It’s just you so far"
          action={
            <button className="btn-primary h-10 py-0 text-[14px]" onClick={onInvite}>
              <UserPlus size={15} aria-hidden="true" /> Invite someone
            </button>
          }
        >
          Invite people by email and pick what they can do. They set their own password and 2FA from
          the link.
        </EmptyState>
      ) : (
        <>
          {/* Phones: one row per person. */}
          <ul className="divide-y divide-hairline md:hidden">
            {list.map((p) => (
              <li key={p.id} className="space-y-2 px-4 py-3.5">
                <div className="flex items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <Who p={p} onOpen={onOpen} />
                  </div>
                  <ChevronRight size={16} className="shrink-0 text-stone" aria-hidden="true" />
                </div>
                <div className="space-y-1.5 pl-[42px]">
                  <RolePills ids={p.roles} roles={data.roles} />
                  <StatusLine p={p} />
                </div>
              </li>
            ))}
          </ul>
          <ScrollX className="hidden md:block">
            <table className="data-table table-compact table-baseline">
              <thead>
                <tr>
                  <th>Person</th>
                  <th>Roles</th>
                  <th>Status</th>
                  <th className="text-right">Last sign-in</th>
                  <th className="w-8">
                    <span className="sr-only">Open</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {list.map((p) => (
                  <tr
                    key={p.id}
                    onClick={() => onOpen(p.id)}
                    className={`cursor-pointer ${p.status === 'removed' ? 'text-stone' : ''}`}
                  >
                    <td className="max-w-[280px]">
                      <Who p={p} onOpen={onOpen} />
                    </td>
                    <td className="max-w-[320px]">
                      <RolePills ids={p.roles} roles={data.roles} />
                    </td>
                    <td>
                      <StatusLine p={p} />
                    </td>
                    <td className="whitespace-nowrap text-right text-[13px] text-stone">
                      {p.lastSignInAt ? <TimeAgo t={p.lastSignInAt} /> : 'Never'}
                    </td>
                    <td className="text-right text-stone">
                      <ChevronRight size={16} aria-hidden="true" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollX>
        </>
      )}
      {(data.removed > 0 || showRemoved) && (
        <div className="border-t border-hairline px-4 py-2.5 text-[13px] sm:px-5">
          <button
            type="button"
            className="-my-1 rounded-[6px] px-1 py-1 font-semibold text-graphite underline decoration-ink/25 underline-offset-2 transition max-sm:min-h-9 hover:text-ink hover:decoration-ink"
            onClick={onToggleRemoved}
          >
            {showRemoved
              ? 'Hide removed people'
              : `Show ${plural(data.removed, 'removed person', 'removed people')}`}
          </button>
        </div>
      )}
    </div>
  );
}

/** Pick roles: each with what it is for and how much it can do. Owner first. */
function RolePicker({
  roles,
  value,
  onChange,
  idPrefix,
}: {
  roles: Role[];
  value: string[];
  onChange: (ids: string[]) => void;
  idPrefix: string;
}) {
  return (
    <fieldset>
      <legend className="label">Roles</legend>
      <ul className="divide-y divide-hairline overflow-hidden rounded-[12px] border border-hairline bg-paper">
        {roles.map((r) => {
          const on = value.includes(r.id);
          // A role they already have stays visible even when the person looking can't give it.
          const locked = !r.grantable;
          const id = `${idPrefix}-${r.id}`;
          return (
            <li key={r.id}>
              <label
                htmlFor={id}
                className={`flex gap-3 px-3.5 py-3 ${locked ? 'cursor-not-allowed' : 'cursor-pointer hover:bg-canvas/60'}`}
              >
                <input
                  id={id}
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 shrink-0 accent-ink"
                  checked={on}
                  disabled={locked}
                  onChange={(e) =>
                    onChange(e.target.checked ? [...value, r.id] : value.filter((x) => x !== r.id))
                  }
                />
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className={`text-[14px] font-bold ${locked ? 'text-stone' : ''}`}>
                      {r.name}
                    </span>
                    <span className="text-[12px] tabular-nums text-stone">
                      {r.id === OWNER ? 'Everything' : plural(r.permissions.length, 'permission')}
                    </span>
                  </span>
                  {r.description && (
                    <span className="mt-0.5 block text-[13px] leading-snug text-stone">
                      {r.description}
                    </span>
                  )}
                  {locked && (
                    <span className="mt-1 block text-[12px] font-semibold text-graphite">
                      {r.id === OWNER
                        ? 'Only an owner can give this role.'
                        : 'Includes permissions you don’t have.'}
                    </span>
                  )}
                </span>
              </label>
            </li>
          );
        })}
      </ul>
    </fieldset>
  );
}

const same = (a: string[], b: string[]) => [...a].sort().join() === [...b].sort().join();

/** Every permission these roles give together (the owner role's list is all of them). */
const permsOfRoles = (ids: string[], roles: Role[]) =>
  new Set(ids.flatMap((id) => roles.find((r) => r.id === id)?.permissions ?? []));
/** The strongest permissions in `next` that `before` doesn't have: giving them takes a 2FA code. */
const strongGains = (next: Iterable<Permission>, before: Iterable<Permission> = []) => {
  const want = new Set(next);
  const had = new Set(before);
  return POWERFUL_PERMISSIONS.filter((p) => want.has(p) && !had.has(p));
};

/**
 * Making someone an owner, giving one of the strongest permissions, or enabling an owner again
 * takes a fresh code from your own authenticator (the API checks it; the owners get an email).
 */
function OwnerCode({
  id,
  value,
  onChange,
  gives,
  label = 'Your 2FA code, to make them an owner',
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
  /** The strongest permissions being given, when that's why the code is asked for. */
  gives?: Permission[];
  label?: string;
}) {
  return (
    <div className="rounded-[12px] border border-hairline bg-paper p-3.5">
      <label className="label" htmlFor={id}>
        {gives?.length ? 'Your 2FA code, to give strong permissions' : label}
      </label>
      <p className="mb-2.5 text-[13px] leading-snug text-stone">
        {gives?.length
          ? `${gives.map((p) => PERMISSIONS[p].label).join('; ')}. These come close to everything, so the owners get an email about it.`
          : 'Owners can do everything here, including changing and removing other owners. The other owners get an email about it.'}
      </p>
      <input
        id={id}
        className="input w-40 font-semibold tabular-nums tracking-[0.3em] placeholder:font-normal"
        inputMode="numeric"
        autoComplete="one-time-code"
        placeholder="••••••"
        maxLength={6}
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, ''))}
      />
    </div>
  );
}

function PersonDrawer({ p, data, onClose }: { p: Person; data: TeamData; onClose: () => void }) {
  const qc = useQueryClient();
  const [roles, setRoles] = useState(p.roles);
  const refresh = () => qc.invalidateQueries({ queryKey: ['team'] });
  const changed = !same(roles, p.roles);
  const active = p.status !== 'removed';

  const [code, setCode] = useState('');
  const promoting = roles.includes(OWNER) && !p.roles.includes(OWNER);
  const gives = promoting
    ? []
    : strongGains(permsOfRoles(roles, data.roles), permsOfRoles(p.roles, data.roles));
  const needsCode = promoting || gives.length > 0;
  const saveRoles = async () => {
    const ok = await run(
      () =>
        api(`/team/${p.id}/roles`, {
          method: 'PUT',
          body: { roles, ...(needsCode ? { code } : {}) },
        }),
      promoting ? `${p.name} is now an owner.` : `${p.name}’s roles are saved.`,
    );
    setCode('');
    if (ok) await refresh();
  };
  // An owner comes back with everything: enabling one takes your code too.
  const [enableCode, setEnableCode] = useState('');
  const disabledOwner = p.status === 'disabled' && p.roles.includes(OWNER);
  const act = async (
    action: 'disable' | 'enable' | 'reset' | 'remove' | 'resend',
    done: string,
    ask?: { title: string; body: string; confirmLabel: string },
    body: Record<string, unknown> = {},
  ) => {
    if (ask && (await confirmAction({ ...ask, tone: 'danger' })) === null) return;
    const ok = await run(() => api(`/team/${p.id}/${action}`, { body }), done);
    await refresh();
    if (ok && action === 'remove') onClose();
  };

  const why = p.you
    ? 'This is you. Someone else on the team changes your roles or sign-in.'
    : p.status === 'removed'
      ? 'Removed people can’t come back as they were. Invite their address again to give them a fresh account.'
      : p.roles.includes(OWNER) && !data.owner
        ? 'Only an owner can change an owner.'
        : 'They can do things you can’t, so an owner changes them.';

  return (
    <Drawer
      onClose={onClose}
      title={p.name}
      subtitle={p.email}
      headerRight={<Pill tone={STATUS[p.status].tone}>{STATUS[p.status].label}</Pill>}
      footer={
        p.manageable ? (
          <div className="flex flex-wrap gap-2">
            {p.status === 'invited' && (
              <button
                className="btn-ghost h-10 px-4 py-0 text-[14px]"
                onClick={() =>
                  void act('resend', 'New invite sent. The earlier link no longer works.')
                }
              >
                <Mail size={15} aria-hidden="true" /> Resend invite
              </button>
            )}
            {p.status === 'active' && (
              <button
                className="btn-ghost h-10 px-4 py-0 text-[14px]"
                onClick={() =>
                  void act('reset', `${p.name} is signed out and has a new setup link.`, {
                    title: `Reset ${p.name}’s password and 2FA?`,
                    body: 'They’re signed out now, and their password and authenticator stop working. We email them a new setup link (it works for 3 days) to choose a password and add the admin to their authenticator again.',
                    confirmLabel: 'Reset and email a link',
                  })
                }
              >
                <KeyRound size={15} aria-hidden="true" /> Reset password and 2FA
              </button>
            )}
            {p.status === 'disabled' ? (
              <button
                className="btn-ghost h-10 px-4 py-0 text-[14px]"
                disabled={disabledOwner && enableCode.length !== 6}
                onClick={() =>
                  void act(
                    'enable',
                    `${p.name} can sign in again.`,
                    undefined,
                    disabledOwner ? { code: enableCode } : {},
                  ).finally(() => setEnableCode(''))
                }
              >
                <Power size={15} aria-hidden="true" /> Enable
              </button>
            ) : (
              <button
                className="btn-ghost h-10 px-4 py-0 text-[14px]"
                onClick={() =>
                  void act('disable', `${p.name} is disabled and signed out.`, {
                    title: `Disable ${p.name}?`,
                    body: 'They’re signed out now and can’t sign in until someone enables them again. Their roles stay as they are.',
                    confirmLabel: 'Disable',
                  })
                }
              >
                <Ban size={15} aria-hidden="true" /> Disable
              </button>
            )}
            <button
              className="btn-ghost h-10 px-4 py-0 text-[14px] text-berry-deep hover:border-berry-deep sm:ml-auto"
              onClick={() =>
                void act('remove', `${p.name} is off the team.`, {
                  title: `Remove ${p.name} from the team?`,
                  body: 'They’re signed out and can never sign in to this account again; their roles, password and 2FA are deleted. Their name stays in the audit log. To bring them back later, invite their address again.',
                  confirmLabel: 'Remove from team',
                })
              }
            >
              <UserMinus size={15} aria-hidden="true" /> Remove
            </button>
          </div>
        ) : (
          <ViewOnly>{why}</ViewOnly>
        )
      }
    >
      <div className="space-y-5">
        <section className="card p-4 sm:p-5">
          <KV
            items={[
              [
                'Sign-in',
                p.status === 'invited'
                  ? 'Hasn’t set a password and 2FA yet'
                  : p.twoFactor
                    ? 'Password and 2FA set up'
                    : '—',
              ],
              ['Last sign-in', p.lastSignInAt ? <TimeAgo key="l" t={p.lastSignInAt} /> : 'Never'],
              [
                p.status === 'invited' ? 'Invite sent' : 'Invited',
                <span key="i">
                  <TimeAgo t={p.invitedAt} />
                  {p.invitedBy && <span className="font-normal text-stone"> by {p.invitedBy}</span>}
                </span>,
              ],
              ...(p.removedAt
                ? ([['Removed', <TimeAgo key="r" t={p.removedAt} />]] as [ReactNode, ReactNode][])
                : []),
            ]}
          />
        </section>

        {disabledOwner && p.manageable && (
          <OwnerCode
            id={`${p.id}-enable-code`}
            value={enableCode}
            onChange={setEnableCode}
            label="Your 2FA code, to enable an owner again"
          />
        )}
        {active && p.manageable ? (
          <section className="space-y-3">
            <RolePicker roles={data.roles} value={roles} onChange={setRoles} idPrefix={p.id} />
            {needsCode && (
              <OwnerCode id={`${p.id}-code`} value={code} onChange={setCode} gives={gives} />
            )}
            <div className="flex items-center gap-3">
              <button
                className="btn-primary h-10 px-4 py-0 text-[14px]"
                onClick={() => void saveRoles()}
                disabled={!changed || roles.length === 0 || (needsCode && code.length !== 6)}
              >
                <Check size={15} aria-hidden="true" /> Save roles
              </button>
              <span className="text-[13px] text-stone">
                {roles.length === 0
                  ? 'Pick at least one role. To take all access away, disable them.'
                  : changed
                    ? 'Applies on their next click.'
                    : ''}
              </span>
            </div>
          </section>
        ) : (
          <section>
            <h3 className="label">Roles</h3>
            <RolePills ids={p.roles} roles={data.roles} />
          </section>
        )}
      </div>
    </Drawer>
  );
}

function InviteDrawer({ data, onClose }: { data: TeamData; onClose: () => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ name: '', email: '', roles: [] as string[] });
  const [code, setCode] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const owner = f.roles.includes(OWNER);
  const gives = owner ? [] : strongGains(permsOfRoles(f.roles, data.roles));
  const needsCode = owner || gives.length > 0;
  const ready =
    f.name.trim() &&
    /^\S+@\S+\.\S+$/.test(f.email.trim()) &&
    f.roles.length > 0 &&
    (!needsCode || code.length === 6);
  const send = async () => {
    setErr(null);
    try {
      await api('/team/invite', {
        body: { ...f, email: f.email.trim(), ...(needsCode ? { code } : {}) },
      });
      toast(`Invite sent to ${f.email.trim()}. The link works for 3 days.`);
      await qc.invalidateQueries({ queryKey: ['team'] });
      onClose();
    } catch (e) {
      setErr((e as Error).message);
      setCode('');
    }
  };
  return (
    <Drawer
      onClose={onClose}
      title="Invite someone"
      subtitle="They get an email with a link to set their password and 2FA. It works for 3 days."
      footer={
        <div className="flex flex-wrap items-center gap-3">
          <button
            className="btn-primary h-10 px-4 py-0 text-[14px]"
            onClick={() => void send()}
            disabled={!ready}
          >
            <Mail size={15} aria-hidden="true" /> Send invite
          </button>
          {err ? (
            <span role="alert" className="text-[13px] font-semibold text-berry-deep">
              {err}
            </span>
          ) : (
            !ready && (
              <span className="text-[13px] text-stone">
                {needsCode
                  ? 'Name, email, roles and your 2FA code.'
                  : 'Name, email and at least one role.'}
              </span>
            )
          )}
        </div>
      }
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (ready) void send();
        }}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="label" htmlFor="invite-name">
              Name
            </label>
            <input
              id="invite-name"
              className="input"
              autoComplete="off"
              maxLength={60}
              value={f.name}
              onChange={(e) => setF({ ...f, name: e.target.value })}
            />
          </div>
          <div>
            <label className="label" htmlFor="invite-email">
              Email
            </label>
            <input
              id="invite-email"
              className="input"
              type="email"
              autoComplete="off"
              value={f.email}
              onChange={(e) => setF({ ...f, email: e.target.value })}
            />
          </div>
        </div>
        <RolePicker
          roles={data.roles}
          value={f.roles}
          onChange={(roles) => setF({ ...f, roles })}
          idPrefix="invite"
        />
        {needsCode && <OwnerCode id="invite-code" value={code} onChange={setCode} gives={gives} />}
        <button type="submit" hidden />
      </form>
    </Drawer>
  );
}

/* ------------------------------------------------------------------ Roles */

function RolesList({ data, onOpen }: { data: TeamData; onOpen: (id: string) => void }) {
  return (
    <ul className="card divide-y divide-hairline overflow-hidden">
      {data.roles.map((r) => (
        <li key={r.id}>
          <button
            type="button"
            onClick={() => onOpen(r.id)}
            className="flex w-full items-center gap-4 px-4 py-3.5 text-left transition hover:bg-[#fbf8f3] sm:px-5"
          >
            <span className="min-w-0 flex-1">
              <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className="text-[15px] font-bold">{r.name}</span>
                {r.builtIn ? <Pill>Built-in</Pill> : <Pill tone="sky">Custom</Pill>}
              </span>
              {r.description && (
                <span className="mt-0.5 block text-[13px] leading-snug text-stone">
                  {r.description}
                </span>
              )}
              <span className="mt-1 block text-[12px] tabular-nums text-stone sm:hidden">
                {holders(r.members)} · {reach(r)}
              </span>
            </span>
            <span className="hidden shrink-0 text-right text-[13px] tabular-nums text-stone sm:block">
              <span className="block font-semibold text-ink">{holders(r.members)}</span>
              {reach(r)}
            </span>
            <ChevronRight size={16} className="shrink-0 text-stone" aria-hidden="true" />
          </button>
        </li>
      ))}
    </ul>
  );
}

/** The permission catalogue by area: ticks to edit a custom role, or what a role includes. */
function PermissionGroups({
  value,
  onChange,
  mine,
}: {
  value: Permission[];
  /** Absent: read-only, only the permissions in `value` are listed. */
  onChange?: (v: Permission[]) => void;
  /** What the person editing has: only an owner may add what they lack. */
  mine?: (p: Permission) => boolean;
}) {
  const areas = onChange
    ? AREAS
    : AREAS.map((a) => ({ ...a, perms: a.perms.filter((p) => value.includes(p)) })).filter(
        (a) => a.perms.length,
      );
  return (
    <div className="space-y-4">
      {areas.map((a) => (
        <fieldset key={a.area}>
          <legend className="mb-1.5 text-[13px] font-bold text-graphite">{a.area}</legend>
          <ul className="divide-y divide-hairline overflow-hidden rounded-[12px] border border-hairline bg-paper">
            {a.perms.map((p) => {
              const on = value.includes(p);
              if (!onChange)
                return (
                  <li key={p} className="flex items-start gap-2.5 px-3.5 py-2.5 text-[14px]">
                    <Check
                      size={15}
                      className="mt-0.5 shrink-0 text-mint-deep"
                      aria-hidden="true"
                    />
                    {PERMISSIONS[p].label}
                  </li>
                );
              const locked = !!mine && !mine(p);
              return (
                <li key={p}>
                  <label
                    className={`flex items-start gap-2.5 px-3.5 py-2.5 text-[14px] ${locked ? 'cursor-not-allowed text-stone' : 'cursor-pointer hover:bg-canvas/60'}`}
                    title={locked ? 'You don’t have this permission yourself.' : undefined}
                  >
                    <input
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 shrink-0 accent-ink"
                      checked={on}
                      disabled={locked}
                      onChange={(e) =>
                        onChange(e.target.checked ? [...value, p] : value.filter((x) => x !== p))
                      }
                    />
                    <span className="min-w-0">{PERMISSIONS[p].label}</span>
                  </label>
                </li>
              );
            })}
          </ul>
        </fieldset>
      ))}
    </div>
  );
}

function RoleDrawer({
  role,
  data,
  onClose,
}: {
  /** null: a new role. */
  role: Role | null;
  data: TeamData;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { data: me } = useQuery(staffQuery);
  const mine = (p: Permission) => data.owner || !!me?.permissions.includes(p);
  const [f, setF] = useState({
    name: role?.name ?? '',
    description: role?.description ?? '',
    permissions: role?.permissions ?? ([] as Permission[]),
  });
  const [err, setErr] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const gives = strongGains(f.permissions, role?.permissions ?? []);
  const editing = !role || role.editable;
  const dirty =
    !role ||
    f.name !== role.name ||
    f.description !== (role.description ?? '') ||
    !same(f.permissions, role.permissions);
  const ready = f.name.trim() && f.permissions.length > 0 && (!gives.length || code.length === 6);

  const save = async () => {
    setErr(null);
    try {
      await api(role ? `/team/roles/${role.id}` : '/team/roles', {
        method: role ? 'PUT' : 'POST',
        body: { ...f, ...(gives.length ? { code } : {}) },
      });
      toast(role ? `${f.name.trim()} is saved.` : `${f.name.trim()} is ready to give to people.`);
      await qc.invalidateQueries({ queryKey: ['team'] });
      onClose();
    } catch (e) {
      setErr((e as Error).message);
      setCode('');
    }
  };
  const remove = async () => {
    if (!role) return;
    const ok = await confirmAction({
      title: `Delete the ${role.name} role?`,
      body: 'Nobody has it, so nobody loses access. This can’t be undone.',
      confirmLabel: 'Delete role',
      tone: 'danger',
    });
    if (ok === null) return;
    if (
      await run(
        () => api(`/team/roles/${role.id}`, { method: 'DELETE', body: {} }),
        'Role deleted.',
      )
    ) {
      await qc.invalidateQueries({ queryKey: ['team'] });
      onClose();
    }
  };

  const holders = role ? data.staff.filter((s) => s.roles.includes(role.id)) : [];
  return (
    <Drawer
      onClose={onClose}
      title={role ? role.name : 'New role'}
      subtitle={
        !role
          ? 'Pick what people with this role can see and do. You can change it later.'
          : role.builtIn
            ? 'Built-in roles come with the admin and can’t be edited. For anything else, make a custom role.'
            : `Custom role · ${plural(role.members, 'person', 'people')}`
      }
      headerRight={role && (role.builtIn ? <Pill>Built-in</Pill> : <Pill tone="sky">Custom</Pill>)}
      footer={
        editing ? (
          <div className="flex flex-wrap items-center gap-3">
            <button
              className="btn-primary h-10 px-4 py-0 text-[14px]"
              onClick={() => void save()}
              disabled={!ready || !dirty}
            >
              <Check size={15} aria-hidden="true" /> {role ? 'Save role' : 'Create role'}
            </button>
            {err && (
              <span role="alert" className="text-[13px] font-semibold text-berry-deep">
                {err}
              </span>
            )}
            {role && (
              <span className="sm:ml-auto">
                {role.members > 0 ? (
                  <span className="text-[13px] text-stone">
                    Take it off its {plural(role.members, 'person', 'people')} to delete it.
                  </span>
                ) : (
                  <button
                    className="btn-ghost h-10 px-4 py-0 text-[14px] text-berry-deep hover:border-berry-deep"
                    onClick={() => void remove()}
                  >
                    Delete role
                  </button>
                )}
              </span>
            )}
          </div>
        ) : role && !role.builtIn ? (
          <ViewOnly>
            It includes permissions you don’t have, or someone who has it can do more than you, so
            an owner changes it.
          </ViewOnly>
        ) : undefined
      }
    >
      <div className="space-y-5">
        {editing && (
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="label" htmlFor="role-name">
                Name
              </label>
              <input
                id="role-name"
                className="input"
                maxLength={40}
                placeholder="e.g. Night shift"
                value={f.name}
                onChange={(e) => setF({ ...f, name: e.target.value })}
              />
            </div>
            <div>
              <label className="label" htmlFor="role-description">
                What it’s for <span className="font-normal text-stone">(optional)</span>
              </label>
              <input
                id="role-description"
                className="input"
                maxLength={200}
                value={f.description}
                onChange={(e) => setF({ ...f, description: e.target.value })}
              />
            </div>
          </div>
        )}
        {role?.id === OWNER ? (
          <p className="card p-4 text-[14px] leading-relaxed text-graphite sm:p-5">
            Owners have every permission, including ones added later, and are the only people who
            can make or change other owners. The team always keeps at least one active owner.
          </p>
        ) : (
          <PermissionGroups
            value={f.permissions}
            {...(editing
              ? { onChange: (permissions: Permission[]) => setF({ ...f, permissions }), mine }
              : {})}
          />
        )}
        {editing && gives.length > 0 && (
          <OwnerCode id="role-code" value={code} onChange={setCode} gives={gives} />
        )}
        {holders.length > 0 && (
          <section>
            <h3 className="label">Who has it</h3>
            <p className="text-[14px] text-graphite">{holders.map((h) => h.name).join(', ')}</p>
          </section>
        )}
      </div>
    </Drawer>
  );
}
